import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import useMobileModalMode from '../../hooks/useMobileModalMode';
import { RECONCILIATION_CURRENCIES, REVIEW_INTERVAL_PRESETS, formatInterval } from '../../utils/reconciliationUtils';

const emptyForm = {
    name: '',
    taxNumber: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    defaultCurrency: 'USD',
    reviewIntervalDays: '',
    notes: ''
};

const CompanyModal = ({ isOpen, onClose, onRefresh, editData = null, defaultIntervalDays = 90 }) => {
    const [formData, setFormData] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useMobileModalMode(isOpen);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (editData) {
            setFormData({
                name: editData.name || '',
                taxNumber: editData.taxNumber || '',
                contactName: editData.contactName || '',
                contactPhone: editData.contactPhone || '',
                contactEmail: editData.contactEmail || '',
                defaultCurrency: editData.defaultCurrency || 'USD',
                reviewIntervalDays: editData.reviewIntervalDays ? String(editData.reviewIntervalDays) : '',
                notes: editData.notes || ''
            });
        } else {
            setFormData(emptyForm);
        }
    }, [isOpen, editData]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Firma adı zorunludur.');
            return;
        }

        const payload = {
            ...formData,
            reviewIntervalDays: formData.reviewIntervalDays === '' ? null : Number(formData.reviewIntervalDays)
        };

        setIsSubmitting(true);
        try {
            if (editData) {
                await api.put(`/reconciliations/companies/${editData.id}`, payload);
                toast.success('Firma güncellendi.');
            } else {
                await api.post('/reconciliations/companies', payload);
                toast.success('Firma eklendi.');
            }

            if (onRefresh) onRefresh();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'İşlem tamamlanamadı.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    const inputClass = 'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary-500 text-dark-text';
    const labelClass = 'block text-sm font-medium text-dark-muted mb-1';

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
            <div className="bg-dark-card border border-dark-border w-full max-w-2xl text-dark-text rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl">

                <div className="flex justify-between items-center p-5 sm:p-6 border-b border-dark-border bg-dark-bg/40">
                    <h2 className="text-xl font-bold">{editData ? 'Firma Düzenle' : 'Yeni Firma'}</h2>
                    <button onClick={onClose} className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-full transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 overflow-y-auto">
                    <form id="reconciliationCompanyForm" onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className={labelClass}>Firma Ünvanı <span className="text-red-400">*</span></label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="Örn: Öz Yürek Tekstil San. Tic. Ltd. Şti." />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Vergi No / VKN</label>
                                <input type="text" name="taxNumber" value={formData.taxNumber} onChange={handleChange} className={inputClass} placeholder="1234567890" />
                            </div>
                            <div>
                                <label className={labelClass}>Muhasebe Yetkilisi</label>
                                <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className={inputClass} placeholder="Örn: Ayşe Demir" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Telefon</label>
                                <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className={inputClass} placeholder="05xx xxx xx xx" />
                            </div>
                            <div>
                                <label className={labelClass}>E-posta</label>
                                <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className={inputClass} placeholder="muhasebe@firma.com" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Varsayılan Para Birimi</label>
                                <select name="defaultCurrency" value={formData.defaultCurrency} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
                                    {RECONCILIATION_CURRENCIES.map((currency) => (
                                        <option key={currency} value={currency}>{currency}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Mutabakat Sıklığı</label>
                                <select name="reviewIntervalDays" value={formData.reviewIntervalDays} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
                                    <option value="">Varsayılan ({formatInterval(defaultIntervalDays).toLowerCase()})</option>
                                    {REVIEW_INTERVAL_PRESETS.map((days) => (
                                        <option key={days} value={days}>{formatInterval(days)} ({days} gün)</option>
                                    ))}
                                    {formData.reviewIntervalDays !== '' && !REVIEW_INTERVAL_PRESETS.includes(Number(formData.reviewIntervalDays)) && (
                                        <option value={formData.reviewIntervalDays}>{formData.reviewIntervalDays} günde bir</option>
                                    )}
                                </select>
                                <p className="mt-1 text-xs text-dark-muted">Son mutabakattan bu kadar gün sonra uyarı verilir.</p>
                            </div>
                        </div>

                        <div>
                            <label className={labelClass}>Not</label>
                            <textarea name="notes" rows="2" value={formData.notes} onChange={handleChange} className={inputClass} placeholder="Firma hakkında not" />
                        </div>
                    </form>
                </div>

                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-dark-border bg-dark-card/95 flex justify-end gap-3 pb-safe-offset">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-dark-muted hover:text-dark-text hover:bg-dark-hover transition-colors">
                        Vazgeç
                    </button>
                    <button type="submit" form="reconciliationCompanyForm" disabled={isSubmitting} className="px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-bold transition-colors disabled:opacity-50">
                        {isSubmitting ? 'Kaydediliyor...' : (editData ? 'Güncelle' : 'Firma Ekle')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompanyModal;
